import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Share, CheckCircle2, Smartphone } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useNavigate } from "react-router-dom";

export default function Install() {
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const [isIOS, setIsIOS] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);
  }, []);

  const handleInstall = async () => {
    const success = await promptInstall();
    if (success) {
      setTimeout(() => navigate("/"), 1000);
    }
  };

  if (isInstalled) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <CardTitle>Already Installed!</CardTitle>
            <CardDescription>
              RoofGenius AI is already installed on your device
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={() => navigate("/")} className="w-full">
              Open App
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Smartphone className="w-8 h-8 text-primary" />
          </div>
          <CardTitle>Install RoofGenius AI</CardTitle>
          <CardDescription>
            Get the full app experience on your device
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!isIOS && isInstallable ? (
            // Android/Chrome - Show install button
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground text-center">
                Install RoofGenius AI for offline access, faster loading, and a native app experience.
              </div>
              <Button onClick={handleInstall} className="w-full" size="lg">
                <Download className="mr-2 h-5 w-5" />
                Install App
              </Button>
            </div>
          ) : isIOS ? (
            // iOS - Show manual instructions
            <div className="space-y-4">
              <div className="text-sm font-medium">To install on iOS:</div>
              <ol className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">1</span>
                  <span>Tap the <Share className="inline h-4 w-4 mx-1" /> Share button at the bottom of Safari</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">2</span>
                  <span>Scroll down and tap "Add to Home Screen"</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">3</span>
                  <span>Tap "Add" in the top right corner</span>
                </li>
              </ol>
              <div className="pt-2">
                <Button onClick={() => navigate("/")} variant="outline" className="w-full">
                  Continue in Browser
                </Button>
              </div>
            </div>
          ) : (
            // Fallback for browsers that don't support PWA install
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground text-center">
                Your browser doesn't support app installation yet. Continue using RoofGenius AI in your browser.
              </div>
              <Button onClick={() => navigate("/")} className="w-full">
                Continue in Browser
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
